<template>
  <div
    v-if="dropdownType === 'dropdown'"
    class="dropdown"
    ref="dropdownMenu"
    :class="{ 'is-active': menuOpen }"
    @keypress.space.prevent
    @keydown.up.prevent.stop="focusPreviousMenuItem()"
    @keydown.down.prevent.stop="focusNextMenuItem()"
  >
    <div class="dropdown-trigger">
      <rbz-form-button
        variant="tertiary"
        :icon="triggerButtonIcon"
        :icon-props="triggerButtonIconProps"
        :skip-focus-clear="true"
        :classes="['menu-icon', triggerButtonClasses].join(' ')"
        data-qa="menu-icon-button"
        @button-click="menuOpen ? closeMenu() : openMenu()"
        :ignore-update-in-process="true"
      />
      <slot name="dropdownTriggerButton" />
    </div>
    <div class="dropdown-menu" role="menubar">
      <div class="dropdown-menu-content">
        <slot name="dropdownMenuContent" />
      </div>
    </div>
  </div>
  <div
    v-if="dropdownType === 'contextmenu' && menuOpen"
    class="context-menu"
    :style="{ top: contextMenuPosition[0], left: contextMenuPosition[1] }"
    role="menubar"
    tabindex="-1"
    ref="contextMenu"
    @keypress.space.prevent
    @keydown.up.stop="focusPreviousMenuItem()"
    @keydown.down.stop="focusNextMenuItem()"
  >
    <div class="context-menu-content">
      <slot name="contextMenuContent" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';
import _ from 'lodash';
import RbzFormButton from '@/components/RbzFormButton.vue';
import { GenericObject } from '@/types';

interface DropdownMenu {
  dropdownType?: 'dropdown' | 'contextmenu';
  triggerButtonIcon?: string;
  triggerButtonIconProps?: GenericObject;
  triggerButtonClasses?: string;
  contextMenuPosition?: [string, string];
}

const props = withDefaults(defineProps<DropdownMenu>(), {
  dropdownType: 'dropdown',
});

const menuOpen = ref(false);
const dropdownMenu = ref(null);
const contextMenu = ref(null);

const focusPreviousMenuItem = () => {
  let indexToFocus;
  const menuRef = props.dropdownType === 'dropdown' ? dropdownMenu : contextMenu;
  const focusableElements = menuRef.value.querySelectorAll("[tabindex='0']");
  _.forEach(focusableElements, (element, index) => {
    if (element.contains(document.activeElement)) {
      indexToFocus = index - 1;
    }
  });
  if (!_.isNil(indexToFocus) && indexToFocus >= 0) {
    focusableElements[indexToFocus].focus();
  }
};

const focusNextMenuItem = () => {
  let indexToFocus;
  const menuRef = props.dropdownType === 'dropdown' ? dropdownMenu : contextMenu;
  const focusableElements = menuRef.value.querySelectorAll("[tabindex='0']");
  _.forEach(focusableElements, (element, index) => {
    if (element.contains(document.activeElement)) {
      indexToFocus = index + 1;
    }
  });
  if (_.isNil(indexToFocus)) {
    indexToFocus = 0;
  }
  if (!_.isNil(indexToFocus) && indexToFocus < focusableElements.length) {
    focusableElements[indexToFocus].focus();
  }
};

const openMenu = () => {
  menuOpen.value = true;
  document.addEventListener('click', closeMenuListener);
  document.addEventListener('keydown', closeMenuListener, true);
  if (props.dropdownType === 'contextmenu') {
    nextTick(() => {
      contextMenu.value?.querySelectorAll("[tabindex='0']")[0].focus();
    });
  }
};

const closeMenu = () => {
  menuOpen.value = false;
  document.removeEventListener('click', closeMenuListener);
  document.removeEventListener('keydown', closeMenuListener, true);
};

const closeMenuListener = (event: Event) => {
  const isMouseClick = (event as MouseEvent).pointerType === 'mouse';
  const isEscape = (event as KeyboardEvent).code === 'Escape';
  const menuRef = props.dropdownType === 'dropdown' ? dropdownMenu : contextMenu;
  if ((isMouseClick && !menuRef.value?.contains(event.target)) || isEscape) {
    closeMenu();
  }
  if (isEscape) {
    event.stopPropagation();
  }
};

defineExpose({
  openMenu,
  closeMenu,
  dropdownMenu,
  contextMenu,
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

$dropdown-max-width: 240px;

.dropdown-menu {
  left: auto;
  max-width: $dropdown-max-width;
  min-width: max-content;
  overflow: visible;
  padding-top: 12px;
  right: 0;
  z-index: 100005;
}

:deep(.dropdown-menu-content) {
  align-items: flex-start;
  background: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'silver');
  border-radius: 4px;
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: visible;
  padding: 0 0 4px;
  position: relative;

  > * {
    width: 100%;
  }
}

.context-menu {
  background: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'boulder');
  display: block;
  list-style: none;
  margin: 0;
  overflow: visible;
  padding: 0;
  position: fixed;
  z-index: 100005;
}

:deep(.context-menu-content) {
  align-items: flex-start;
  background: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'silver');
  border-radius: 4px;
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: visible;
  padding: 0 0 4px;
  position: relative;

  > * {
    width: 100%;
  }
}
</style>
